import React, { useEffect, useMemo, useState } from 'react';
import { onValue, query, ref, limitToLast, orderByChild } from 'firebase/database';
import { FileText, User as UserIcon, Clock } from 'lucide-react';
import { rtdb } from '../../firebase';
import { useEstablishmentAuth } from '../../context/EstablishmentAuthContext';
import './establishment.css';

function formatDate(ts) {
  if (!ts) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(ts));
}

function LogsPage() {
  const { adminDoc } = useEstablishmentAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const q = query(ref(rtdb, 'adminLogs'), orderByChild('timestamp'), limitToLast(200));
    return onValue(q, (snap) => {
      const rows = [];
      snap.forEach((c) => {
        const v = c.val();
        if (v) rows.push({ id: c.key, ...v });
      });
      rows.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setLogs(rows);
      setLoading(false);
    });
  }, []);

  const scoped = useMemo(() => {
    if (!adminDoc) return [];
    if (adminDoc.role === 'super') return logs;
    const allowed = adminDoc.crousIds || {};
    return logs.filter((l) => l.crousId && allowed[l.crousId] === true);
  }, [logs, adminDoc]);

  const filtered = useMemo(() => {
    if (filter === 'all') return scoped;
    return scoped.filter((l) => l.action === filter);
  }, [scoped, filter]);

  return (
    <div className="est-page">
      <div className="est-page-head">
        <div>
          <span className="est-kicker">Audit</span>
          <h1>Logs d'activité</h1>
          <p>Historique des actions admin (overrides appliqués ou levés).</p>
        </div>
        <div className="est-range-picker">
          <button
            type="button"
            className={`est-chip ${filter === 'all' ? 'is-selected' : ''}`}
            onClick={() => setFilter('all')}
          >
            Tout
          </button>
          <button
            type="button"
            className={`est-chip ${filter === 'set_override' ? 'is-selected' : ''}`}
            onClick={() => setFilter('set_override')}
          >
            Overrides appliqués
          </button>
          <button
            type="button"
            className={`est-chip ${filter === 'remove_override' ? 'is-selected' : ''}`}
            onClick={() => setFilter('remove_override')}
          >
            Overrides levés
          </button>
        </div>
      </div>

      {loading && <div className="est-empty">Chargement des logs…</div>}

      {!loading && filtered.length === 0 && (
        <div className="est-empty">
          Aucune action enregistrée pour l'instant. Les logs apparaîtront ici dès la première
          surécriture.
        </div>
      )}

      <div className="est-logs">
        {filtered.map((l) => (
          <div key={l.id} className="est-log">
            <div className="est-log-icon">
              <FileText size={16} />
            </div>
            <div className="est-log-body">
              <div className="est-log-title">
                <strong>
                  {l.action === 'set_override' ? 'Override appliqué' : 'Override levé'}
                </strong>
                <span className="est-log-ru">{l.crousId}</span>
                {l.status && <span className="est-log-status">{l.status}</span>}
              </div>
              {l.message && <div className="est-log-message">« {l.message} »</div>}
              <div className="est-log-meta">
                <span>
                  <UserIcon size={12} /> {l.adminName || l.adminId}
                </span>
                <span>
                  <Clock size={12} /> {formatDate(l.timestamp)}
                </span>
                {l.expiresAt && (
                  <span>Expire {formatDate(l.expiresAt)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LogsPage;
